import type { TeamMember } from '@nemo/api';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

/** Today as `YYYY-MM-DD` in the viewer's own calendar. */
function today(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function useEscape(onCancel: () => void): void {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      onCancel();
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onCancel]);
}

/** The same dialog frame `NewListModal` uses, around the fields one question needs. */
function Dialog(props: {
  title: string;
  onCancel: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  submitLabel: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const titleId = useId();
  useEscape(props.onCancel);
  return createPortal(
    <div
      className="modal-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) props.onCancel();
      }}
    >
      <form
        className="modal add-to-list"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(event) => {
          event.preventDefault();
          if (props.canSubmit) props.onSubmit();
        }}
      >
        <h2 id={titleId} className="modal-title">
          {props.title}
        </h2>
        {props.children}
        <div className="modal-actions">
          <button className="button" type="button" onClick={props.onCancel}>
            Cancel
          </button>
          <button className="button primary" type="submit" disabled={!props.canSubmit}>
            {props.submitLabel}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

/**
 * Somebody joins: their name and Project A address, and the day if known.
 * Their Affinity person is linked by the address, now if an earlier read holds
 * it, otherwise by the next Affinity sync.
 */
export function AddMemberModal(props: {
  pending: boolean;
  onAdd: (member: { name: string; email: string; joinedOn: string | null }) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [joinedOn, setJoinedOn] = useState('');
  const canAdd = name.trim() !== '' && email.includes('@') && !props.pending;
  return (
    <Dialog
      title="Add a team member"
      onCancel={props.onCancel}
      onSubmit={() =>
        props.onAdd({
          name: name.trim(),
          email: email.trim(),
          joinedOn: joinedOn === '' ? null : joinedOn,
        })
      }
      canSubmit={canAdd}
      submitLabel="Add member"
    >
      <label className="field">
        <span className="subtle">Name</span>
        <input
          autoFocus
          value={name}
          placeholder="Full name"
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label className="field">
        <span className="subtle">Email</span>
        <input
          type="email"
          value={email}
          placeholder="first.last@project-a.vc"
          onChange={(event) => setEmail(event.target.value)}
        />
      </label>
      <label className="field">
        <span className="subtle">Joined (optional)</span>
        <input type="date" value={joinedOn} onChange={(event) => setJoinedOn(event.target.value)} />
      </label>
    </Dialog>
  );
}

/**
 * Somebody leaves, on a day. What they knew stays on file, labelled as theirs,
 * and stops counting as a way in.
 */
export function MarkLeftModal(props: {
  member: TeamMember;
  pending: boolean;
  onConfirm: (leftOn: string) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [leftOn, setLeftOn] = useState(today());
  const sole = props.member.soleFunds.length;
  return (
    <Dialog
      title={`Mark ${props.member.name} as left?`}
      onCancel={props.onCancel}
      onSubmit={() => props.onConfirm(leftOn)}
      canSubmit={leftOn !== '' && !props.pending}
      submitLabel="Mark as left"
    >
      <p className="subtle">
        Their {props.member.relationshipCount} relationships stay on file, shown as former team, and
        stop counting as a way in.
        {sole > 0 &&
          ` ${sole} tracked fund${sole === 1 ? '' : 's'} where nobody else current knows them well will read “Only former or support staff know them”.`}
      </p>
      <label className="field">
        <span className="subtle">Left on</span>
        <input type="date" value={leftOn} onChange={(event) => setLeftOn(event.target.value)} />
      </label>
    </Dialog>
  );
}
